/**
 * Centralized state management for Calculator using useReducer
 */
import { useReducer } from 'react';
import type { CalculatorState, CalculatorAction } from '../types/calculator.types';

/**
 * Initial state for calculator
 */
const initialState: CalculatorState = {
  results: null,
  loading: false,
  error: null,
  currentTariff: 'G11',
  baselineTariff: 'G11',
  formValues: null,
  calculationYear: 2026,
  selectedOSD: 'TAURON',
  shareUrl: null,
  shareHash: null,
  saveStatus: 'idle',
  saveError: undefined,
  initialFormValues: null,
  initialSharedData: null,
  urlParamsApplied: false,
};

/**
 * Reducer for calculator state
 */
function calculatorReducer(state: CalculatorState, action: CalculatorAction): CalculatorState {
  switch (action.type) {
    case 'SET_RESULTS':
      return { ...state, results: action.payload, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_CURRENT_TARIFF':
      return { ...state, currentTariff: action.payload };
    case 'SET_BASELINE_TARIFF':
      return { ...state, baselineTariff: action.payload };
    case 'SET_FORM_VALUES':
      return { ...state, formValues: action.payload };
    case 'SET_CALCULATION_YEAR':
      return { ...state, calculationYear: action.payload };
    case 'SET_SELECTED_OSD':
      return { ...state, selectedOSD: action.payload };
    case 'SET_SHARE_URL':
      return { ...state, shareUrl: action.payload };
    case 'SET_SHARE_HASH':
      return { ...state, shareHash: action.payload };
    case 'SET_SAVE_STATUS':
      return { ...state, saveStatus: action.payload };
    case 'SET_SAVE_ERROR':
      return { ...state, saveError: action.payload };
    case 'SET_INITIAL_FORM_VALUES':
      return { ...state, initialFormValues: action.payload };
    case 'SET_INITIAL_SHARED_DATA':
      return { ...state, initialSharedData: action.payload };
    case 'SET_URL_PARAMS_APPLIED':
      return { ...state, urlParamsApplied: action.payload };
    case 'RESET_SAVE_STATE':
      return { ...state, saveStatus: 'idle', saveError: undefined };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

/**
 * Custom hook for calculator state management
 */
export function useCalculatorState() {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);
  return { state, dispatch };
}
